import { useState } from "react";
import { Ban, Share2, Trash2 } from "lucide-react";
import type { DashboardLink, DashboardLinkStatus } from "@shared/types/dashboard";
import { useDashboardLinks } from "../../lib/api/useDashboardOverview";
import { useDismissSharedLink, useRevokeSharedLink } from "../../lib/api/useSharedLinks";
import { ApiError } from "../../lib/api/client";
import { useToast } from "../../lib/toast/ToastProvider";
import { cn } from "../../lib/cn";
import { DialogShell } from "../ui/DialogShell";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";

/**
 * The dashboard's Shared care links popup (BRIEF §5.6): every share link the parent
 * has not deleted, across all their children, with who it is for, what it
 * shares, when it was made, last opened and expires, and where it stands.
 *
 *  - Cancel = revoke (DELETE /api/shared-links/:id). The link stops working at
 *    once and stays in the list as "Cancelled".
 *  - Delete = revoke if it still works, then hide it from every list
 *    (POST /api/shared-links/:id/dismiss). Never a hard delete: the access
 *    history is kept.
 *
 * Both ask first. Statuses come from the server, computed with the same
 * predicate as the tile's count, so the "Active" group always matches the tile.
 */
export function ActiveLinksDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: links, isLoading, error } = useDashboardLinks(open);
  const [pending, setPending] = useState<{ link: DashboardLink; action: "cancel" | "delete" } | null>(
    null,
  );
  // Reuses the child Sharing tab's revoke hook; it now also refreshes the
  // dashboard. The link's own child id keys its optimistic per-child update.
  const revoke = useRevokeSharedLink(pending?.link.childId ?? "");
  const dismiss = useDismissSharedLink();
  const { publish } = useToast();

  const active = (links ?? []).filter((l) => l.status === "active");
  const ended = (links ?? []).filter((l) => l.status !== "active");

  const confirm = async () => {
    if (!pending) return;
    const { link, action } = pending;
    try {
      if (action === "cancel") {
        await revoke.mutateAsync(link.id);
        publish({
          variant: "success",
          title: "Link cancelled",
          description: `${link.caregiverName} can no longer open ${link.childName}'s care profile.`,
        });
      } else {
        await dismiss.mutateAsync(link.id);
        publish({ variant: "success", title: "Link deleted" });
      }
    } catch (err) {
      publish({
        variant: "error",
        title: action === "cancel" ? "Couldn't cancel the link" : "Couldn't delete the link",
        description: err instanceof ApiError ? err.message : "Please try again.",
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <DialogShell
        open={open}
        onOpenChange={onOpenChange}
        title="Your shared care links"
        description="Who can open your children's care profiles right now, and the links that have ended."
        size="xl"
      >
        <div className="mt-6" data-shot="active-links-dialog">
          {isLoading && (
            <div role="status" className="flex items-center justify-center gap-2 py-8 text-sm text-neutral-600">
              <Spinner className="h-4 w-4" /> Loading…
            </div>
          )}
          {error && (
            <p className="rounded-xl bg-red-50 px-4 py-6 text-center text-sm text-red-700">
              Couldn&apos;t load your links right now. Close this and try again.
            </p>
          )}
          {links && links.length === 0 && (
            <EmptyState
              size="sm"
              icon={Share2}
              accent="violet"
              title="No shared care links yet"
              description="Create one from a child's Sharing tab when you need to hand over their care information."
            />
          )}
          {links && links.length > 0 && (
            <div className="max-h-[60vh] space-y-6 overflow-y-auto pr-1">
              <LinkGroup
                heading={`Active (${active.length})`}
                empty="No link is active right now."
                links={active}
                onCancel={(link) => setPending({ link, action: "cancel" })}
                onDelete={(link) => setPending({ link, action: "delete" })}
              />
              {ended.length > 0 && (
                <LinkGroup
                  heading={`Ended (${ended.length})`}
                  links={ended}
                  onCancel={(link) => setPending({ link, action: "cancel" })}
                  onDelete={(link) => setPending({ link, action: "delete" })}
                />
              )}
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        </div>
      </DialogShell>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(next) => {
          if (!next) setPending(null);
        }}
        title={pending?.action === "delete" ? "Delete this link?" : "Cancel this link?"}
        description={confirmCopy(pending)}
        confirmLabel={pending?.action === "delete" ? "Delete link" : "Cancel link"}
        cancelLabel="Keep it"
        destructive
        isPending={revoke.isPending || dismiss.isPending}
        onConfirm={() => {
          void confirm();
        }}
      />
    </>
  );
}

function confirmCopy(pending: { link: DashboardLink; action: "cancel" | "delete" } | null): string {
  if (!pending) return "";
  const { link, action } = pending;
  if (action === "cancel") {
    return `${link.caregiverName} will no longer be able to open ${link.childName}'s care profile. The link stops working straight away.`;
  }
  return link.status === "active"
    ? `The link stops working straight away and disappears from your lists. ${link.caregiverName} will no longer be able to open ${link.childName}'s care profile.`
    : "It disappears from your lists. A record of when it was opened is kept.";
}

function LinkGroup({
  heading,
  empty,
  links,
  onCancel,
  onDelete,
}: {
  heading: string;
  empty?: string;
  links: DashboardLink[];
  onCancel: (link: DashboardLink) => void;
  onDelete: (link: DashboardLink) => void;
}) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
        {heading}
      </h3>
      {links.length === 0 ? (
        <p className="rounded-2xl bg-white/60 px-4 py-4 text-sm text-neutral-600 ring-1 ring-sky-100/80">
          {empty}
        </p>
      ) : (
        <ul className="space-y-3">
          {links.map((link) => (
            <LinkRow key={link.id} link={link} onCancel={onCancel} onDelete={onDelete} />
          ))}
        </ul>
      )}
    </section>
  );
}

const STATUS_PILL: Record<DashboardLinkStatus, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-emerald-100 text-emerald-800" },
  expired: { label: "Expired", className: "bg-amber-100 text-amber-800" },
  cancelled: { label: "Cancelled", className: "bg-neutral-100 text-neutral-700" },
};

function LinkRow({
  link,
  onCancel,
  onDelete,
}: {
  link: DashboardLink;
  onCancel: (link: DashboardLink) => void;
  onDelete: (link: DashboardLink) => void;
}) {
  const pill = STATUS_PILL[link.status];
  const sections = sharedSectionLabels(link.visibleSections);
  const live = link.status === "active";

  return (
    <li
      className={cn(
        // Soft rows: live links bright with a violet ring, ended ones faded back
        // (rings, not shadows: the list scrolls and would clip a shadow).
        "rounded-2xl p-4",
        live ? "bg-white ring-1 ring-violet-200" : "bg-white/55 ring-1 ring-sky-100",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <p className="break-words font-medium text-neutral-900">{link.caregiverName}</p>
          <p className="text-sm text-neutral-600">For {link.childName}</p>
        </div>
        <span
          className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold", pill.className)}
        >
          {pill.label}
        </span>
      </div>

      <p className="mt-2 text-sm text-neutral-700">
        <span className="font-medium">Shares:</span>{" "}
        {sections.length > 0 ? sections.join(", ") : "Nothing yet"}
      </p>

      {/* Phones: the dates stack, actions below. From `sm` up: one row. */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-neutral-600 sm:flex-1 sm:grid-cols-3">
          <div className="flex gap-1 sm:block">
            <dt className="font-semibold text-neutral-700">Created</dt>
            <dd>{formatDate(link.createdAt)}</dd>
          </div>
          <div className="flex gap-1 sm:block">
            <dt className="font-semibold text-neutral-700">Last opened</dt>
            <dd>{link.lastAccessedAt ? formatRelative(link.lastAccessedAt) : "Not opened yet"}</dd>
          </div>
          <div className="flex gap-1 sm:block">
            <dt className="font-semibold text-neutral-700">{endLabel(link)}</dt>
            <dd>{endValue(link)}</dd>
          </div>
        </dl>

        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          {live && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onCancel(link)}
              aria-label={`Cancel the link for ${link.caregiverName} (${link.childName})`}
            >
              <Ban className="h-4 w-4" aria-hidden />
              Cancel
            </Button>
          )}
          <Button
            variant="destructiveOutline"
            size="sm"
            onClick={() => onDelete(link)}
            aria-label={`Delete the link for ${link.caregiverName} (${link.childName})`}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * Plain labels for the sections a link shares, in the share dialog's order.
 * Keyed by string (not the VisibleSections type) so a section key added later
 * (e.g. `legalName`) still gets a label here — and an unknown one a readable
 * fallback — instead of silently vanishing from the list.
 */
const SECTION_LABELS: Record<string, string> = {
  basic: "Basic info",
  legalName: "Legal name",
  emergencyContacts: "Emergency contacts",
  allergies: "Allergies",
  medications: "Medications",
  carePreferences: "Care preferences",
  routines: "Daily routines",
  feeding: "Feeding & diet",
  medical: "Medical & immunizations",
  pickup: "Pickup & authorization",
};

function sharedSectionLabels(sections: DashboardLink["visibleSections"]): string[] {
  const entries = Object.entries(sections as Record<string, boolean | undefined>);
  const order = Object.keys(SECTION_LABELS);
  return entries
    .filter(([, on]) => on === true)
    .map(([key]) => key)
    .sort((a, b) => indexOrEnd(order, a) - indexOrEnd(order, b))
    .map((key) => SECTION_LABELS[key] ?? humanize(key));
}

function indexOrEnd(order: string[], key: string): number {
  const i = order.indexOf(key);
  return i === -1 ? order.length : i;
}

function humanize(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelative(iso: string): string {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatDate(iso);
}

function endLabel(link: DashboardLink): string {
  if (link.status === "cancelled") return "Cancelled";
  if (link.status === "expired") return "Expired";
  return "Expires";
}

function endValue(link: DashboardLink): string {
  if (link.status === "cancelled") return link.revokedAt ? formatDate(link.revokedAt) : "Yes";
  if (!link.expiresAt) return "Never";
  return formatDate(link.expiresAt);
}
